import React from "react";
import { Link } from "react-router-dom";
import { useBranding } from "@/context/BrandingContext";
import { imageUrl } from "@/lib/api";

// Icon brand tenant: pakai logo tenant (settings/template snapshot). Legacy
// default "/brand/logo-horizontal.svg" (Sinar Telur) tetap memakai mark.svg;
// tenant tanpa logo → mark netral Dynicty (bukan telur).
function brandIconSrc(visualLogo) {
  if (visualLogo === "/brand/logo-horizontal.svg") return "/brand/mark.svg";
  if (visualLogo) return imageUrl(visualLogo);
  return "/brand/dynicty-mark.svg";
}

/**
 * Brand Logo — branding-aware via BrandingContext.
 * Menampilkan nama & tagline tenant dari Branding Engine.
 *
 * Variants:
 *   - "mark"        : icon only (square)
 *   - "horizontal"  : icon + brand name + tagline
 *   - "compact"     : icon + name (no tagline)
 *   - "admin"       : icon + name + ADMIN label
 *   - "stacked"     : icon centered above name (for auth pages)
 */
export default function Logo({
  variant = "compact",
  size = "md",
  to = "/",
  className = "",
  invert = false,
  showTagline = true,
}) {
  const { branding } = useBranding();
  const name = branding.general.name;
  const tagline = branding.general.tagline;
  const iconSrc = brandIconSrc(branding.visual.logo);

  const sizes = {
    sm: { icon: 28, title: "text-base", sub: "text-[9px]" },
    md: { icon: 36, title: "text-lg", sub: "text-[10px]" },
    lg: { icon: 44, title: "text-xl", sub: "text-[10px]" },
    xl: { icon: 56, title: "text-2xl", sub: "text-[11px]" },
  };
  const s = sizes[size] || sizes.md;
  const titleColor = invert ? "text-white" : "text-[color:var(--brand-secondary)]";
  const subColor = invert ? "text-white/70" : "text-[color:var(--brand-primary)]";

  const inner = (
    <span className={`inline-flex items-center gap-2 ${className}`} data-brand-name={name}>
      <img
        src={iconSrc}
        alt={name}
        width={s.icon}
        height={s.icon}
        className="shrink-0"
        style={{ width: s.icon, height: s.icon }}
      />
      {variant !== "mark" && (
        <span className="flex flex-col leading-tight">
          <span className={`font-display font-extrabold tracking-tight ${s.title} ${titleColor}`}>
            {name}
          </span>
          {showTagline && (
            <span className={`uppercase tracking-[0.18em] font-bold ${s.sub} ${subColor}`}>
              {variant === "admin" ? "ADMIN" : tagline}
            </span>
          )}
        </span>
      )}
    </span>
  );

  if (!to) return inner;
  return <Link to={to} className="inline-flex items-center" data-testid="brand-logo">{inner}</Link>;
}

export function LogoStacked({ size = "lg", invert = false }) {
  const { branding } = useBranding();
  const name = branding.general.name;
  const tagline = branding.general.tagline;
  const s = size === "xl" ? 64 : size === "lg" ? 56 : 48;
  return (
    <Link to="/" className="flex items-center justify-center flex-col gap-2" data-testid="brand-logo-stacked">
      <img src={brandIconSrc(branding.visual.logo)} alt={name} width={s} height={s} className="shrink-0 object-contain" style={{ width: s, height: s }} />
      <div className="text-center">
        <div className={`font-display font-extrabold leading-none ${invert ? "text-white" : "text-[color:var(--brand-secondary)]"} text-2xl`}>
          {name}
        </div>
        <div className={`text-[10px] uppercase tracking-[0.2em] font-bold mt-1 ${invert ? "text-white/70" : "text-[color:var(--brand-primary)]"}`}>
          {tagline}
        </div>
      </div>
    </Link>
  );
}
